/* GCS Sprachnachricht (Produktseite, Runde D 04.10.2026) – <gcs-voice> aus sections/gcs-product-voice.liquid.
   Audio lädt erst beim ersten Play (src wird dann gesetzt, preload none). Fortschritt färbt die Balken der statischen
   Wellenform, Tippen/Ziehen auf die Welle spult (vor dem ersten Play wird die Stelle gemerkt, ohne zu laden).
   Nur ein Player spielt gleichzeitig (Event gcs:voice:play); startet ein anderes Audio/Video, pausiert er.
   Tastatur: Play-Button nativ, Welle als Regler (Pfeile ±5 s, Bild auf/ab ±10 s, Pos1/Ende, Leertaste spielt/pausiert).
   Position: liegt die Section nicht im Slot unter der Galerie (Inline-Script in gcs-main-product), verschiebt sie sich
   selbst dorthin; im Theme-Editor auch nach Neu-Rendern und Umsortieren.
   Video-Memo (B2, 17.10.2026): Hat die Section ein Video (data-video-src), spielt Play das Video statt des Audios.
   Das Profilbild wird dabei an Ort und Stelle zum runden Video-Kreis (Klasse is-video-on), ein Ring zeigt den
   Fortschritt, Tippen auf den Kreis oder Pause hält an und minimiert den Kreis wieder zum Profilbild (Play setzt an
   derselben Stelle fort), am Ende kommt das Profilbild zurück. src und Poster werden
   erst beim Tap gesetzt. Lädt das Video nicht, fällt der Player auf das Audio zurück. this.media ist das aktive
   Element (Video oder Audio), Welle, Zeit und Tastatur arbeiten damit gleich. */
(() => {
  if (customElements.get('gcs-voice')) return;

  const fmt = (s) => {
    const t = Math.max(0, Math.floor(Number(s) || 0));
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
  };

  /* Section-Wrapper in den Slot unter der Galerie hängen (true = verschoben) */
  const place = (el) => {
    if (el.closest('[data-gcs-voice-slot]')) return false;
    const slot = document.querySelector('[data-gcs-voice-slot]');
    if (!slot) return false;
    slot.appendChild(el.closest('.shopify-section') || el);
    return true;
  };

  class GcsVoice extends HTMLElement {
    connectedCallback() {
      if (place(this)) return; /* Verschieben verbindet das Element neu */
      this.hidden = false;
      if (this._ready) return;
      this._ready = true;

      this.audio = this.querySelector('[data-gcs-voice-audio]');
      this.video = this.dataset.videoSrc ? this.querySelector('[data-gcs-voice-video]') : null;
      this.circle = this.querySelector('[data-gcs-voice-circle]');
      this.ring = this.querySelector('[data-gcs-voice-ring]');
      this.media = this.video || this.audio;
      this.kind = this.video ? 'Video-Nachricht' : 'Sprachnachricht';
      /* Video-Quellen: abgeleitete 480er zuerst, dann die von Shopify gelistete Fassung */
      this.videoSrcs = [this.dataset.videoAlt, this.dataset.videoSrc].filter(Boolean);
      this.videoIdx = 0;
      this.btn = this.querySelector('[data-gcs-voice-toggle]');
      this.wave = this.querySelector('[data-gcs-voice-wave]');
      this.bars = Array.from(this.querySelectorAll('.gcs-voice__bar'));
      this.timeEl = this.querySelector('[data-gcs-voice-time]');
      this.errorEl = this.querySelector('[data-gcs-voice-error]');
      this.more = this.querySelector('[data-gcs-voice-more]');
      this.text = this.querySelector('[data-gcs-voice-text]');
      this.name = this.dataset.name || 'Max';
      this.total = Number(this.dataset.seconds) || 30;
      this.pos = 0;
      this.pending = null;
      this.played = -1;
      if (!this.audio || !this.btn || !this.wave) return;

      this.btn.addEventListener('click', () => this.toggle());
      if (this.video && this.circle) this.circle.addEventListener('click', () => this.toggle());
      if (this.more && this.text) {
        this.more.addEventListener('click', () => {
          const open = this.more.getAttribute('aria-expanded') !== 'true';
          this.more.setAttribute('aria-expanded', String(open));
          this.text.hidden = !open;
        });
      }

      this.bindMedia(this.audio);
      if (this.video) this.bindMedia(this.video);

      /* Nur ein Player: andere Sprachnachrichten und jedes andere Audio/Video auf der Seite pausieren diese */
      document.addEventListener('gcs:voice:play', (e) => {
        if (e.detail !== this && !this.media.paused) this.media.pause();
      });
      document.addEventListener(
        'play',
        (e) => {
          if (e.target !== this.media && !this.media.paused) this.media.pause();
        },
        true
      );

      this.bindWave();
    }

    /* Ereignisse für Audio und Video; nur das aktive Element (this.media) zählt */
    bindMedia(a) {
      const on = (type, fn) => a.addEventListener(type, (e) => {
        if (a === this.media) fn(e);
      });
      on('loadedmetadata', () => {
        if (Number.isFinite(a.duration) && a.duration > 0) {
          this.total = a.duration;
          this.wave.setAttribute('aria-valuemax', String(Math.round(this.total)));
        }
        if (this.pending !== null) {
          a.currentTime = Math.min(this.pending, Math.max(0, this.total - 0.25));
          this.pending = null;
        }
        this.paint(true);
      });
      on('play', () => this.setState('playing'));
      on('playing', () => this.setBusy(false));
      on('waiting', () => this.setBusy(true));
      on('pause', () => {
        this.setState('paused');
        /* Review PR #11 (David, 17.10.2026): Pause minimiert den Video-Kreis sofort wieder zum Profilbild, Play setzt an
           derselben Stelle fort. Das Ende (a.ended) läuft weiter über done(), damit ohne Eingabe nichts springt. */
        if (a === this.video && !a.ended) this.collapse();
      });
      on('ended', () => {
        this.setState('paused');
        this.pos = 0;
        try {
          a.currentTime = 0;
        } catch (e) {
          /* ignorieren */
        }
        this.paint(true, true);
        if (a === this.video) this.done();
      });
      on('timeupdate', () => {
        if (a.paused) this.paint();
      });
      on('error', () => (a === this.video ? this.videoError() : this.fail()));
    }

    /* Video-Quelle lädt nicht: nächste Quelle, danach Audio */
    videoError() {
      if (this.videoIdx + 1 < this.videoSrcs.length) {
        this.videoIdx += 1;
        if (this.pos > 0 && this.pending === null) this.pending = this.pos;
        this.video.removeAttribute('src');
        this.play();
      } else {
        this.fallback();
      }
    }

    /* Ende: Video und Ring blenden aus, im Kreis steht wieder das Profilbild. Der Kreis schrumpft erst bei der
       nächsten Eingabe (Tippen, Taste) oder wenn der Block aus dem Bild ist. So verschiebt das Zurückspringen nie
       ohne Eingabe etwas (kein Layout-Shift, CLS bleibt 0). */
    done() {
      this.classList.add('is-video-done');
      const collapse = (e) => {
        if (e && e.target && e.type !== 'io' && this.contains(e.target)) return; /* Tap auf den Player: spielt neu */
        this.collapse();
      };
      this._collapse = collapse;
      document.addEventListener('pointerdown', collapse, true);
      document.addEventListener('keydown', collapse, true);
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          if (entries.some((en) => !en.isIntersecting)) collapse({ type: 'io' });
        });
        this._io.observe(this);
      }
    }

    collapse() {
      this.classList.remove('is-video-on', 'is-video-done');
      this.disarm();
    }

    disarm() {
      if (this._collapse) {
        document.removeEventListener('pointerdown', this._collapse, true);
        document.removeEventListener('keydown', this._collapse, true);
        this._collapse = null;
      }
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
    }

    /* Video lädt nicht: auf das Audio des Typs zurückfallen (gleiche Position), sonst Fehlertext */
    fallback() {
      if (!this.video || this.media !== this.video) return;
      this.disarm();
      this.classList.remove('is-video-on', 'is-video-done', 'gcs-voice--video');
      this.media = this.audio;
      this.kind = 'Sprachnachricht';
      if (!this.dataset.src) {
        this.fail();
        return;
      }
      this.total = Number(this.dataset.seconds) || this.total;
      this.play();
    }

    get loaded() {
      return !!this.media.getAttribute('src');
    }

    toggle() {
      if (this.media.paused) this.play();
      else this.media.pause();
    }

    play() {
      this.hideError();
      document.dispatchEvent(new CustomEvent('gcs:voice:play', { detail: this }));
      const m = this.media;
      if (!this.loaded) {
        this.setBusy(true);
        if (m === this.video) {
          if (m.dataset.poster) m.poster = m.dataset.poster;
          m.src = this.videoSrcs[this.videoIdx];
        } else {
          m.src = this.dataset.src;
        }
        if (this.pos > 0 && this.pending === null) this.pending = this.pos;
      }
      if (m === this.video) {
        /* Kopfhöhe merken: Name und Zeile wachsen beim Umbruch nach oben statt den Block höher zu machen */
        const head = this.querySelector('.gcs-voice__head');
        if (head && !this.classList.contains('is-video-on')) this.style.setProperty('--gcs-vm-head', `${head.offsetHeight + 6}px`);
        this.disarm();
        this.classList.remove('is-video-done');
        this.classList.add('is-video-on');
        const img = this.querySelector('[data-gcs-voice-avatar-img]');
        if (img && img.sizes !== '88px') img.sizes = '88px'; /* scharfes Profilbild im großen Kreis (88 px seit 24.10.2026) */
      }
      const p = m.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          this.setBusy(false);
          if (err && err.name === 'NotAllowedError') {
            if (m === this.video && m.paused) this.classList.remove('is-video-on');
            return;
          }
          if (err && err.name === 'AbortError') return;
          if (m === this.video) this.videoError();
          else this.fail();
        });
      }
    }

    setState(state) {
      const playing = state === 'playing';
      this.classList.toggle('is-playing', playing);
      this.btn.setAttribute(
        'aria-label',
        playing ? `${this.kind} von ${this.name} anhalten` : `${this.kind} von ${this.name} abspielen`
      );
      if (playing) this.loop();
      else {
        cancelAnimationFrame(this._raf);
        this.setBusy(false);
        this.paint(true);
      }
    }

    setBusy(on) {
      this.classList.toggle('is-loading', on);
      if (on) this.btn.setAttribute('aria-busy', 'true');
      else this.btn.removeAttribute('aria-busy');
    }

    loop() {
      cancelAnimationFrame(this._raf);
      const step = () => {
        this.paint();
        if (!this.media.paused) this._raf = requestAnimationFrame(step);
      };
      this._raf = requestAnimationFrame(step);
    }

    /* Balken färben, Zeit und Regler-Werte setzen. force = auch ohne Änderung; atStart = Gesamtdauer zeigen */
    paint(force = false, atStart = false) {
      const a = this.media;
      if (this.loaded && this.pending === null && a.readyState >= 1) this.pos = a.currentTime || 0;
      const total = this.total || 1;
      const ratio = Math.max(0, Math.min(1, this.pos / total));
      const count = this.bars.length;
      const played = Math.round(ratio * count);
      if (force || played !== this.played) {
        this.bars.forEach((bar, i) => bar.classList.toggle('is-played', i < played));
        this.played = played;
      }
      if (this.ring && this.media === this.video) {
        const off = (100 * (1 - (atStart ? 0 : ratio))).toFixed(2);
        if (this.ring.style.strokeDashoffset !== off) this.ring.style.strokeDashoffset = off;
      }
      const started = !atStart && (this.pos > 0 || !a.paused);
      const label = started ? fmt(this.pos) : fmt(total);
      if (this.timeEl && this.timeEl.textContent !== label) this.timeEl.textContent = label;
      const now = String(Math.round(this.pos));
      if (force || this.wave.getAttribute('aria-valuenow') !== now) {
        this.wave.setAttribute('aria-valuenow', now);
        this.wave.setAttribute('aria-valuetext', `${fmt(this.pos)} von ${fmt(total)}`);
      }
    }

    seek(seconds) {
      const total = this.total || 1;
      const t = Math.max(0, Math.min(seconds, total));
      this.pos = t;
      if (this.loaded && this.media.readyState >= 1) {
        this.pending = null;
        try {
          this.media.currentTime = t;
        } catch (e) {
          this.pending = t;
        }
      } else {
        this.pending = t;
      }
      this.paint(true);
    }

    bindWave() {
      const at = (event) => {
        const rect = this.wave.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
        return (x / (rect.width || 1)) * (this.total || 1);
      };
      this.wave.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        this.dragging = true;
        try {
          this.wave.setPointerCapture(event.pointerId);
        } catch (e) {
          /* ignorieren */
        }
        this.classList.add('is-seeking');
        this.seek(at(event));
      });
      this.wave.addEventListener('pointermove', (event) => {
        if (this.dragging) this.seek(at(event));
      });
      const end = () => {
        this.dragging = false;
        this.classList.remove('is-seeking');
      };
      this.wave.addEventListener('pointerup', end);
      this.wave.addEventListener('pointercancel', end);
      this.wave.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 5, ArrowUp: 5, ArrowLeft: -5, ArrowDown: -5, PageUp: 10, PageDown: -10 }[event.key];
        if (step !== undefined) {
          event.preventDefault();
          this.seek(this.pos + step);
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.seek(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          this.seek(this.total);
        } else if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          this.toggle();
        }
      });
    }

    fail() {
      this.setBusy(false);
      this.classList.remove('is-playing', 'is-video-on');
      if (this.errorEl) {
        this.errorEl.textContent = `Die ${this.kind} lädt gerade nicht.`;
        this.errorEl.hidden = false;
      }
    }

    hideError() {
      if (this.errorEl && !this.errorEl.hidden) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }
  }

  customElements.define('gcs-voice', GcsVoice);

  /* Theme-Editor: neu gerenderte oder umsortierte Section wieder unter die Galerie */
  const replace = (event) => {
    const root = event.target && event.target.querySelector && event.target.querySelector('gcs-voice');
    if (root && place(root) === false) root.hidden = false;
  };
  document.addEventListener('shopify:section:load', replace);
  document.addEventListener('shopify:section:reorder', replace);
})();
